import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, WHATSAPP_URL, scrollToSection } from "../constants/site";
import { useLang } from "../i18n/LangContext";
import { ShareButton } from "./ShareButton";

const LangToggle = ({ className = "" }) => {
    const { lang, setLang } = useLang();
    return (
        <div className={`flex items-center gap-1.5 font-mono text-[11px] tracking-[0.15em] ${className}`}>
            <button
                data-testid="lang-toggle-pt"
                onClick={() => setLang("pt")}
                className={`transition-colors ${lang === "pt" ? "text-gold" : "text-taupe hover:text-cream"}`}
            >
                PT
            </button>
            <span className="text-burnt/50">/</span>
            <button
                data-testid="lang-toggle-en"
                onClick={() => setLang("en")}
                className={`transition-colors ${lang === "en" ? "text-gold" : "text-taupe hover:text-cream"}`}
            >
                EN
            </button>
        </div>
    );
};

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { t } = useLang();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToSection(id), open ? 250 : 0);
    };

    return (
        <motion.header
            data-testid="navbar"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, delay: 2.8, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
                scrolled
                    ? "border-b border-burnt/15 bg-noir/85 backdrop-blur-md"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12">
                <button
                    data-testid="nav-logo"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="group flex items-center transition-opacity duration-300 hover:opacity-80"
                    aria-label="Yasmim Alves — Nail Designer, voltar ao topo"
                >
                    <img
                        src="/logo-horizontal.png"
                        alt="Yasmim Alves — Nail Designer"
                        className="h-9 w-auto sm:h-10"
                    />
                </button>

                <nav className="hidden items-center gap-7 lg:flex">
                    {NAV_LINKS.map((link, i) => (
                        <button
                            key={link.id}
                            data-testid={`nav-link-${link.id}`}
                            onClick={() => go(link.id)}
                            className="group relative font-sans text-sm tracking-wide text-taupe transition-colors duration-300 hover:text-cream"
                        >
                            {t.nav.links[i]}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                    <LangToggle />
                    <ShareButton
                        testId="nav-share-button"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-beige/25 text-cream transition-all duration-300 hover:border-gold hover:text-gold"
                    />
                    <a
                        data-testid="nav-cta-agendar"
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full bg-gold px-6 py-2.5 text-sm font-medium text-noir transition-all duration-300 hover:scale-[1.03] hover:bg-burnt"
                    >
                        {t.nav.cta}
                    </a>
                </nav>

                <div className="flex items-center gap-4 lg:hidden">
                    <LangToggle />
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="flex h-11 w-11 items-center justify-center rounded-full border border-burnt/25 text-cream"
                        aria-label="Abrir menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="overflow-hidden border-b border-burnt/15 bg-noir/95 backdrop-blur-md lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {NAV_LINKS.map((link, i) => (
                                <button
                                    key={link.id}
                                    data-testid={`mobile-nav-link-${link.id}`}
                                    onClick={() => go(link.id)}
                                    className="py-3 text-left font-serif text-2xl text-cream transition-colors hover:text-gold"
                                >
                                    {t.nav.links[i]}
                                </button>
                            ))}
                            <a
                                data-testid="mobile-nav-cta-agendar"
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-4 rounded-full bg-gold px-6 py-3.5 text-center text-sm font-medium text-noir"
                            >
                                {t.nav.cta}
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Navbar;
